import axios from 'axios'
import Repository from '@/api/core'
import queryString from 'query-string'
import vendor from '@/store/modules/vendor'
import auth from '@/store/modules/auth'

// Assignment layer lives in izteach-online-course (the LEARN service), under /assignment.
// Unit authoring/persistence reuses unitRepository (also LEARN); this repository only owns
// the assign + list + log-create surface.
const baseURL = process.env.VUE_APP_API_LEARN + '/assignment'

const withVendor = (params = {}) =>
  queryString.stringify({ ...params, vendorId: vendor.state.vendor.vendorId })

// POST /assignment — assign a master unit to per-class courses (+ one framework chapter).
// params: { masterUnitId, subjectTopic, gradeTopic, sectionTitle?, courses: [courseId] }
// No chapter chosen -> backend places the copy in the "Uncategorized" section (top).
const assign = (params) => {
  return Repository(baseURL).create(params)
}

// GET /assignment — SERVER-SIDE paginated list of units by lifecycle status.
// params.status: 'assigned' (default, already given to classes) | 'draft' (not assigned yet).
// params also carries pagination: page (1-based) + limit; plus optional subjectTopic/gradeTopic.
// withVendor stringifies the WHOLE params object, so page/limit/status/topics all flow through
// the querystring unchanged (vendorId is appended automatically).
// Response body: { items: [ ...units ], total: <int matching the filter, ignoring page/limit> }
// (Was previously a bare array.)
const fetchAssigned = (params) => {
  return Repository(baseURL + `?${withVendor(params)}`).fetch()
}

// DELETE /assignment/draft/{id} — remove an un-assigned draft unit.
const deleteDraft = (id) => {
  return Repository(baseURL + '/draft').delete(id)
}

// DELETE /assignment/{id} — no course: hard-delete a master unit and every fanned-out
// copy (all classes). With course: un-assign only that one class (removes that copy +
// its target_courses entry, master persists). Use for assigned units; drafts go via
// deleteDraft. The core .delete() puts vendorId last, so the per-class variant issues
// its own axios call to append &course= after the id.
const deleteAssignment = (id, course) => {
  if (!course) return Repository(baseURL).delete(id)
  return axios.delete(baseURL + `/${id}?${withVendor({ course })}`, {
    headers: { Authorization: auth.state.auth.accessToken },
  })
}

// GET /assignment/history — generic ActionLog rows (create/assign).
const fetchHistory = (params) => {
  return Repository(baseURL + `/history?${withVendor(params)}`).fetch()
}

// GET /assignment/course?subjectTopic=&gradeTopic= — per-class target courses for
// a scope (Subject + Grade are Topic ids); the master course is excluded server-side.
const listCourses = ({ subjectTopic, gradeTopic } = {}) => {
  return Repository(baseURL + `/course?${withVendor({ subjectTopic, gradeTopic })}`).fetch()
}

// GET /assignment/my-classes — owner-scoped classes with resolved subject/grade,
// for the grouped-by-grade create modal. fetch() injects vendorId.
const listMyClasses = () => {
  return Repository(baseURL + '/my-classes').fetch()
}

// GET /assignment/section?subjectTopic=&gradeTopic= — framework chapters (sections
// of the master course) for a scope. These are the shared framework chapter options.
const listSections = ({ subjectTopic, gradeTopic } = {}) => {
  return Repository(baseURL + `/section?${withVendor({ subjectTopic, gradeTopic })}`).fetch()
}

// POST /assignment/log-create — record an ActionLog when a master unit is created.
// params: { masterUnitId, title, type, subjectTopic, gradeTopic }
const logCreate = (params) => {
  return Repository(baseURL + '/log-create').create(params)
}

// PUT /assignment/{id} — edit one class's schedule. from* = assign date (ngày giao),
// to* = due date (hạn nộp). Either pair may be omitted for a partial update.
const updateSchedule = ({ id, course, rangeTimeFromDate, rangeTimeFromHour, rangeTimeToDate, rangeTimeToHour } = {}) => {
  return Repository(baseURL).update(id, {
    course,
    rangeTimeFromDate,
    rangeTimeFromHour,
    rangeTimeToDate,
    rangeTimeToHour,
  })
}

export default {
  assign,
  fetchAssigned,
  deleteDraft,
  deleteAssignment,
  fetchHistory,
  listCourses,
  listMyClasses,
  listSections,
  logCreate,
  updateSchedule,
}
